import { produce } from 'immer'
import {
  AccountSettingsActions,
  RESET_ERRORS,
} from 'src/app/AccountSettings/redux/accountSettings.actions'
import { accountSettingsRouteProps } from 'src/navigation/routeProps'
import {
  PUT_CREDIT_CARD_ACCOUNT_INIT,
  PUT_CREDIT_CARD_BILLING_INIT,
  PUT_CREDIT_CARD_FAILURE,
  PUT_CREDIT_CARD_INIT,
  PUT_CREDIT_CARD_SUCCESS,
  PutCreditCardActions,
  REPLACE_CREDIT_CARD_FAILURE,
  REPLACE_CREDIT_CARD_INIT,
  REPLACE_CREDIT_CARD_SUCCESS,
  ReplaceCreditCardActions,
} from 'src/redux/actions/data/creditCards.actions'
import { createBackgroundReducer } from 'src/redux/genericReducers'
import { ErrorDetailMap, parseBackendErrors } from 'src/utils/error.utils'

export interface AccountSettingsState {
  puttingCreditCard: boolean
  puttingCreditCardAccount: boolean
  puttingCreditCardBilling: boolean
  putCreditCardAccountCausedError: boolean
  putCreditCardBillingCausedError: boolean
  putCreditCardTopLevelError?: string
  putCreditCardErrorDetailMap?: ErrorDetailMap<string>
  patchingCreditCard: boolean
  replaceCreditCardTopLevelError?: string
  replaceCreditCardErrorDetailMap?: ErrorDetailMap<string>
}

export const initialState: AccountSettingsState = {
  puttingCreditCard: false,
  puttingCreditCardAccount: false,
  puttingCreditCardBilling: false,
  putCreditCardAccountCausedError: false,
  putCreditCardBillingCausedError: false,
  patchingCreditCard: false,
}

export type Actions =
  | PutCreditCardActions
  | ReplaceCreditCardActions
  | AccountSettingsActions

const CoreAccountSettingsReducer = (
  state = initialState,
  action: Actions,
): AccountSettingsState => {
  return produce(state, (draft) => {
    switch (action.type) {
      case PUT_CREDIT_CARD_INIT: {
        draft.puttingCreditCard = true
        return draft
      }

      case PUT_CREDIT_CARD_ACCOUNT_INIT: {
        draft.puttingCreditCard = true
        draft.puttingCreditCardAccount = true
        return draft
      }

      case PUT_CREDIT_CARD_BILLING_INIT: {
        draft.puttingCreditCard = true
        draft.puttingCreditCardBilling = true
        return draft
      }

      case PUT_CREDIT_CARD_SUCCESS: {
        draft.puttingCreditCard = false
        draft.puttingCreditCardAccount = false
        draft.puttingCreditCardBilling = false
        draft.putCreditCardAccountCausedError = false
        draft.putCreditCardBillingCausedError = false
        draft.putCreditCardTopLevelError = undefined
        draft.putCreditCardErrorDetailMap = undefined
        return draft
      }

      case PUT_CREDIT_CARD_FAILURE: {
        draft.puttingCreditCard = false
        if (state.puttingCreditCardAccount) {
          draft.putCreditCardAccountCausedError = true
        } else if (state.puttingCreditCardBilling) {
          draft.putCreditCardBillingCausedError = true
        }
        draft.puttingCreditCardAccount = false
        draft.puttingCreditCardBilling = false
        const errors = parseBackendErrors(action.payload)
        draft.putCreditCardTopLevelError = errors.topLevelError
        draft.putCreditCardErrorDetailMap = errors.errorDetailMap
        return draft
      }

      case REPLACE_CREDIT_CARD_INIT: {
        draft.puttingCreditCard = true
        draft.patchingCreditCard = true
        return draft
      }

      case REPLACE_CREDIT_CARD_SUCCESS: {
        draft.puttingCreditCard = false
        draft.patchingCreditCard = false
        draft.replaceCreditCardTopLevelError = undefined
        draft.replaceCreditCardErrorDetailMap = undefined
        return draft
      }

      case REPLACE_CREDIT_CARD_FAILURE: {
        draft.puttingCreditCard = false
        draft.patchingCreditCard = false
        const errors = parseBackendErrors(action.payload)
        draft.replaceCreditCardTopLevelError = errors.topLevelError
        draft.replaceCreditCardErrorDetailMap = errors.errorDetailMap
        return draft
      }

      case RESET_ERRORS: {
        draft.replaceCreditCardTopLevelError = undefined
        draft.replaceCreditCardErrorDetailMap = undefined
        return draft
      }
    }
  })
}

const AccountSettingsReducer = createBackgroundReducer(
  accountSettingsRouteProps,
  initialState,
  CoreAccountSettingsReducer,
)

export default AccountSettingsReducer
